import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const empty = { label: "", name: "", company: "", address1: "", address2: "", city: "", state: "", zip: "", country: "US", phone: "", email: "", is_default: false };

export default function AddressBook() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const { data } = await api.get("/addresses");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/addresses/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/addresses", form);
        toast.success("Address added");
      }
      setForm(empty); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    toast.success("Deleted"); load();
  };

  const edit = (a) => {
    const { address_id, user_id, tenant_id, created_at, ...rest } = a;
    setForm(rest); setEditing(address_id); setShowForm(true);
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Address Book</div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Saved addresses</h1>
        </div>
        <button data-testid="add-address-btn" onClick={() => { setShowForm(true); setEditing(null); setForm(empty); }}
          className="brand-bg text-white px-5 py-2 rounded-sm text-sm font-medium flex items-center gap-2 hover:opacity-90">
          <Plus size={16} /> New address
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" data-testid="address-form">
          <div className="font-display text-lg font-bold mb-4">{editing ? "Edit" : "Add"} address</div>
          <div className="grid md:grid-cols-2 gap-3">
            {["label", "name", "company", "address1", "city", "state", "zip", "country", "phone", "email"].map((k) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-wider text-zinc-500">{k}</label>
                <input
                  data-testid={`address-field-${k}`}
                  required={["label", "name", "address1", "city", "state", "zip", "phone"].includes(k)}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)]"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button data-testid="save-address-btn" type="submit" className="brand-bg text-white px-5 py-2 rounded-sm text-sm">Save</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="border border-zinc-300 px-5 py-2 rounded-sm text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <div key={a.address_id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`address-card-${a.address_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="text-xs uppercase tracking-wider brand-text font-bold">{a.label}</div>
              <div className="flex gap-2">
                <button onClick={() => edit(a)} className="text-zinc-500 hover:text-zinc-900"><Pencil size={14} /></button>
                <button data-testid={`delete-address-${a.address_id}`} onClick={() => remove(a.address_id)} className="text-zinc-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="font-medium">{a.name}</div>
            {a.company && <div className="text-sm text-zinc-600">{a.company}</div>}
            <div className="text-sm text-zinc-600 mt-2">{a.address1}</div>
            <div className="text-sm text-zinc-600">{a.city}, {a.state} {a.zip}</div>
            <div className="text-sm text-zinc-500 mt-2 font-mono-plex">{a.phone}</div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="md:col-span-3 py-12 text-center text-zinc-500 text-sm">No addresses yet. Click "New address" to add one.</div>
        )}
      </div>
    </DashboardLayout>
  );
}
